// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, CalendarDays, ShieldBan, Trash2, Loader, AlertTriangle, MapPin, Clock, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  fetchBloqueios,
  createBloqueio,
  deleteBloqueio,
  fetchHorarios,
  BloqueioPayload,
  BloqueioResponse
} from '../../services/api.ts';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import '../../styles/block-manager.css';

const TURNOS = [
  { id: 0, label: 'Almoço', hint: 'Turno diurno' },
  { id: 1, label: 'Jantar', hint: 'Turno noturno' }
];

interface TurnoOption {
  id: number;
  label: string;
  hint: string;
  horarioId: number | null;
  isBlocked: boolean;
}

interface BlockManagementModalProps {
  selectedDate: Date;
  onClose: () => void;
  onChange: () => Promise<void> | void;
}

interface HorarioResumo {
  id: number;
  turno: number;
  hora_inicio: string;
  hora_fim: string;
  dia_semana: number;
  aberto: boolean;
}

const formatDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const mapJsDayToDb = (jsDay: number) => (jsDay + 6) % 7;

const getWeekdayFromKey = (dateKey: string) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  if (!year || !month || !day) return mapJsDayToDb(new Date().getDay());
  const date = new Date(year, month - 1, day);
  return mapJsDayToDb(date.getDay());
};

const formatDisplayDate = (iso: string) => {
  try {
    const parsed = new Date(iso + 'T00:00:00');
    return parsed.toLocaleDateString('pt-BR', {
      weekday: 'short',
      day: '2-digit',
      month: 'short'
    });
  } catch (error) {
    return iso;
  }
};

const getTurnoNome = (turno?: number | null) => {
  if (turno === 0) return 'Almoço';
  if (turno === 1) return 'Jantar';
  return 'Turno';
};

const getIntervaloTurno = (horario?: { hora_inicio?: string | null; hora_fim?: string | null }) => {
  if (!horario?.hora_inicio || !horario?.hora_fim) return null;
  const fmt = (value: string) => value.split(':').slice(0, 2).join(':');
  return `${fmt(horario.hora_inicio)} – ${fmt(horario.hora_fim)}`;
};

const BlockManagementModal: React.FC<BlockManagementModalProps> = ({ selectedDate, onClose, onChange }) => {
  const { floors } = useRestaurant();
  const [blocks, setBlocks] = useState<BloqueioResponse[]>([]);
  const [horariosMap, setHorariosMap] = useState<Record<number, HorarioResumo>>({});
  const [loading, setLoading] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string>('');
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const initialDate = formatDateKey(selectedDate);
  const [formState, setFormState] = useState({
    date: initialDate,
    salaId: '',
    turno: null as number | null,
    horarioId: null as number | null,
    motivo: ''
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [bloqueiosData, horariosData] = await Promise.all([
        fetchBloqueios(),
        fetchHorarios()
      ]);

      setBlocks(bloqueiosData || []);

      const map: Record<number, HorarioResumo> = {};
      (horariosData || []).forEach((horario: any) => {
        if (horario?.id !== undefined && horario?.id !== null) {
          const turnoNumber = Number(horario.turno);
          const diaNumber = Number(horario.dia_semana);
          if (Number.isNaN(turnoNumber) || Number.isNaN(diaNumber)) {
            return;
          }

          map[horario.id] = {
            id: horario.id,
            turno: turnoNumber,
            hora_inicio: horario.hora_inicio,
            hora_fim: horario.hora_fim,
            dia_semana: diaNumber,
            aberto: horario.aberto !== false
          };
        }
      });
      setHorariosMap(map);
    } catch (error: any) {
      console.error('Erro ao carregar bloqueios:', error);
      toast.error('Não foi possível carregar os bloqueios.');
    } finally {
      setLoading(false);
    }
  };

  const dayBlocksForSala = useMemo(() => {
    const targetDate = formState.date || initialDate;
    if (!targetDate) return [] as BloqueioResponse[];
    return (blocks || []).filter(block => {
      if (!block) return false;
      if (block.data !== targetDate) return false;
      if (block.sala_id && formState.salaId && block.sala_id !== formState.salaId) return false;
      return true;
    });
  }, [blocks, formState.date, formState.salaId, initialDate]);

  const availableTurnos = useMemo<TurnoOption[]>(() => {
    const targetDate = formState.date || initialDate;
    if (!targetDate) return [];
    const weekday = getWeekdayFromKey(targetDate);

    const resultado = TURNOS.map(turno => {
      const horarioMatch = Object.values(horariosMap)
        .filter(h => h.dia_semana === weekday && Number(h.turno) === turno.id && h.aberto);

      if (horarioMatch.length === 0) return null;

      const sorted = [...horarioMatch].sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio));
      const primeiro = sorted[0];

      return {
        id: turno.id,
        label: turno.label,
        hint: `${turno.hint} • ${primeiro.hora_inicio.slice(0, 5)} – ${primeiro.hora_fim.slice(0, 5)}`,
        horarioId: primeiro.id,
        isBlocked: false
      } as TurnoOption;
    }).filter(Boolean) as TurnoOption[];

    const blockedTurnos = new Set<number>();
    const blockedHorarios = new Set<number>();
    dayBlocksForSala.forEach(block => {
      if (block.turno !== null && block.turno !== undefined) {
        const parsedTurno = Number(block.turno);
        if (!Number.isNaN(parsedTurno)) blockedTurnos.add(parsedTurno);
      }
      if (block.horario_id !== null && block.horario_id !== undefined) {
        const parsedHorario = Number(block.horario_id);
        if (!Number.isNaN(parsedHorario)) blockedHorarios.add(parsedHorario);
      }
    });

    const finalTurnos = resultado.map(option => ({
      ...option,
      isBlocked:
        (option.horarioId !== null && blockedHorarios.has(option.horarioId)) ||
        blockedTurnos.has(option.id)
    }));

    return finalTurnos;
  }, [formState.date, horariosMap, initialDate, dayBlocksForSala]);

  useEffect(() => {
    if (availableTurnos.length === 0) {
      if (formState.turno !== null || formState.horarioId !== null) {
        setFormState(prev => ({ ...prev, turno: null, horarioId: null }));
      }
      return;
    }

    const availableForSelection = availableTurnos.filter(turno => !turno.isBlocked);

    if (availableForSelection.length === 0) {
      if (formState.turno !== null || formState.horarioId !== null) {
        setFormState(prev => ({ ...prev, turno: null, horarioId: null }));
      }
      return;
    }

    if (formState.turno === null || !availableForSelection.some(turno => turno.id === formState.turno)) {
      const firstAvailable = availableForSelection[0];
      setFormState(prev => ({ ...prev, turno: firstAvailable.id, horarioId: firstAvailable.horarioId }));
      return;
    }

    const current = availableForSelection.find(turno => turno.id === formState.turno);
    if (current && current.horarioId !== formState.horarioId) {
      setFormState(prev => ({ ...prev, horarioId: current.horarioId }));
    }
  }, [availableTurnos, formState.turno, formState.horarioId]);

  useEffect(() => {
    loadData();
    // Carrega bloqueios só no mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeInput = (key: 'date' | 'salaId' | 'turno' | 'motivo', value: string | number | null) => {
    setFormState(prev => {
      if (key === 'date') {
        return {
          ...prev,
          date: String(value ?? ''),
          turno: null,
          horarioId: null
        };
      }

      if (key === 'motivo') {
        return { ...prev, motivo: String(value ?? '') };
      }

      if (key === 'salaId') {
        return {
          ...prev,
          salaId: String(value ?? '')
        };
      }

      if (key === 'turno') {
        return {
          ...prev,
          turno: typeof value === 'number' ? value : null
        };
      }

      return prev;
    });
    setFormError('');
  };

  const handleSelectTurno = (turnoId: number) => {
    const option = availableTurnos.find(opt => opt.id === turnoId);
    if (!option || option.isBlocked) {
      return;
    }
    setFormState(prev => ({
      ...prev,
      turno: option.id,
      horarioId: option.horarioId
    }));
    setFormError('');
  };

  const handleCreateBlock = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formState.salaId) {
      setFormError('Selecione uma sala para bloquear.');
      return;
    }

    const selectedTurno = availableTurnos.find(turno => turno.id === formState.turno && !turno.isBlocked);
    if (!selectedTurno) {
      setFormError('Selecione um turno disponível para esta data.');
      return;
    }

    const payload: BloqueioPayload = {
      data: formState.date,
      sala_id: formState.salaId,
      turno: selectedTurno.id,
      horario_id: selectedTurno.horarioId ?? undefined,
      motivo: formState.motivo?.trim() ? formState.motivo.trim() : undefined
    };

    try {
      setFormSubmitting(true);
      const created = await createBloqueio(payload);
      toast.success('Horário bloqueado com sucesso.');
      setBlocks(prev => [...prev, created]);
      setFormState(prev => ({ ...prev, motivo: '' }));
      await onChange();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Não foi possível criar o bloqueio.';
      toast.error(message);
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = (id: number) => {
    setDeleteTargetId(id);
  };

  const confirmDelete = async () => {
    if (deleteTargetId === null) return;
    setIsDeleting(true);
    try {
      await deleteBloqueio(String(deleteTargetId));
      setBlocks(prev => prev.filter(block => block.id !== deleteTargetId));
      toast.success('Bloqueio removido.');
      setDeleteTargetId(null);
      await onChange();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Erro ao remover bloqueio.';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    if (isDeleting) return;
    setDeleteTargetId(null);
  };

  const getSalaNome = (salaId?: string | null) => {
    if (!salaId) return 'Sala desconhecida';
    const floor = floors.find(f => f.id === salaId);
    return floor ? floor.name : 'Sala desconhecida';
  };

  const getHorarioInfo = (block: BloqueioResponse) => {
    const horario = block.horario_id ? horariosMap[block.horario_id] : null;
    const turno = block.turno ?? horario?.turno ?? null;
    const intervalo = horario ? getIntervaloTurno(horario) : null;
    return {
      turnoLabel: getTurnoNome(turno),
      intervalo
    };
  };

  const isFutureOrToday = (isoDate: string) => {
    const target = new Date(isoDate + 'T23:59:59');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return target >= today;
  };

  const filteredBlocks = useMemo(() => {
    return blocks
      .filter(block => block.data === formState.date && isFutureOrToday(block.data))
      .sort((a, b) => {
        if (a.horario_id && b.horario_id) return a.horario_id - b.horario_id;
        if (a.horario_id) return -1;
        if (b.horario_id) return 1;
        return a.id - b.id;
      });
  }, [blocks, formState.date]);

  const upcomingBlocks = useMemo(() => {
    return blocks
      .filter(block => isFutureOrToday(block.data) && block.data !== formState.date)
      .sort((a, b) => {
        if (a.data === b.data) {
          if (a.horario_id && b.horario_id) return a.horario_id - b.horario_id;
          if (a.horario_id) return -1;
          if (b.horario_id) return 1;
        }
        return a.data.localeCompare(b.data);
      });
  }, [blocks, formState.date]);

  const allTurnosBlocked = availableTurnos.length > 0 && availableTurnos.every(turno => turno.isBlocked);
  const singleTurno = availableTurnos.length === 1 ? availableTurnos[0] : null;

  return (
    <div className="block-manager-overlay">
      <div className="block-manager-modal" role="dialog" aria-modal="true">
        <header className="block-manager-header">
          <div>
            <h3>Bloquear Horários</h3>
            <p>Escolha uma sala e um turno para impedir novas reservas neste dia.</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </button>
        </header>

        <div className="block-manager-body">
          <form className="block-form" onSubmit={handleCreateBlock}>
            <div className="form-grid">
              <label className="form-group">
                <span>Data</span>
                <input
                  type="date"
                  value={formState.date}
                onChange={(e) => handleChangeInput('date', e.target.value)}
                required
              />
            </label>

            <label className="form-group">
              <span>Sala</span>
              <select
                value={formState.salaId}
                onChange={(e) => handleChangeInput('salaId', e.target.value)}
                required
              >
                <option value="">Selecione uma sala</option>
                {floors.map(floor => (
                  <option key={floor.id} value={floor.id}>
                    {floor.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="form-group turno-group">
              <span>Turno</span>
              {availableTurnos.length === 0 ? (
                <div className="turno-empty">
                  Nenhum turno cadastrado para esta data. Ajuste os horários de funcionamento antes de bloquear.
                </div>
              ) : singleTurno ? (
                <div className={`turno-single ${singleTurno.isBlocked ? 'blocked' : ''}`}>
                  <span className="turno-single-label">{singleTurno.label}</span>
                  <small>{singleTurno.hint}</small>
                  {singleTurno.isBlocked && (
                    <small className="turno-status">Já bloqueado para esta data</small>
                  )}
                </div>
              ) : (
                <div className="turno-selector">
                  {availableTurnos.map(option => {
                    const isActive = formState.turno === option.id && formState.horarioId === option.horarioId;
                    return (
                      <button
                        type="button"
                        key={option.id}
                        className={`turno-option ${isActive ? 'active' : ''} ${option.isBlocked ? 'blocked' : ''}`}
                        onClick={() => handleSelectTurno(option.id)}
                        disabled={option.isBlocked}
                        aria-disabled={option.isBlocked}
                      >
                        <span>{option.label}</span>
                        <small>{option.hint}</small>
                        {option.isBlocked && (
                          <small className="turno-option-status">Já bloqueado</small>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {allTurnosBlocked && (
                <div className="turno-empty">
                  Todos os turnos já estão bloqueados para esta data.
                </div>
              )}
            </div>

            <label className="form-group full-width">
              <span>Motivo (opcional)</span>
              <textarea
                rows={2}
                maxLength={120}
                value={formState.motivo}
                onChange={(e) => handleChangeInput('motivo', e.target.value)}
                placeholder="Ex: Manutenção do ambiente"
              />
            </label>
          </div>
          {formError && <div className="form-error">{formError}</div>}

          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={formSubmitting || !formState.salaId || formState.turno === null}
            >
              <Plus size={16} />
              {formSubmitting ? 'Salvando...' : 'Salvar bloqueio'}
            </button>
          </div>
          </form>

          <section className="block-list-section">
            <header>
              <h4>
                <CalendarDays size={16} />
                Bloqueios em {new Date(formState.date + 'T00:00:00').toLocaleDateString('pt-BR', {
                  weekday: 'long',
                  day: '2-digit',
                  month: 'long'
                })}
              </h4>
            </header>

            {loading ? (
              <div className="block-empty">
                <Loader size={20} className="spin" /> Carregando bloqueios...
              </div>
            ) : filteredBlocks.length === 0 ? (
              <div className="block-empty">
                <ShieldBan size={18} /> Nenhum bloqueio cadastrado para esta data.
              </div>
            ) : (
              <ul className="block-list">
                {filteredBlocks.map(block => {
                  const horarioInfo = getHorarioInfo(block);
                  return (
                    <li key={block.id} className="block-card">
                      <div className="block-card-content">
                        <div className="block-card-header">
                          <span className="block-turno">{horarioInfo.turnoLabel}</span>
                          {horarioInfo.intervalo && (
                            <span className="block-intervalo">
                              <Clock size={11} aria-hidden="true" />
                              {horarioInfo.intervalo}
                            </span>
                          )}
                        </div>
                        <div className="block-card-body">
                          <div className="block-field">
                            <span className="block-field-label">Sala</span>
                            <span className="block-field-value">
                              <MapPin size={12} aria-hidden="true" />
                              {getSalaNome(block.sala_id)}
                            </span>
                          </div>
                          {block.motivo && (
                            <div className="block-field">
                              <span className="block-field-label">Motivo</span>
                              <span className="block-field-value">
                                <FileText size={12} aria-hidden="true" />
                                {block.motivo}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="icon-button danger"
                        onClick={() => handleDelete(block.id)}
                        aria-label="Remover bloqueio"
                      >
                        <Trash2 size={16} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="block-upcoming-section">
            <header>
              <h4>Outras datas com bloqueios</h4>
            </header>

            {upcomingBlocks.length === 0 ? (
              <div className="block-empty">
                <ShieldBan size={18} /> Nenhum bloqueio em outras datas futuras.
              </div>
            ) : (
              <ul className="block-list">
                {upcomingBlocks.map(block => {
                  const horarioInfo = getHorarioInfo(block);
                  return (
                    <li key={`upcoming-${block.id}`} className="block-card compact">
                      <div className="block-card-content">
                        <div className="block-card-header">
                          <span className="block-turno">{horarioInfo.turnoLabel}</span>
                          <span className="block-meta">
                            <CalendarDays size={11} aria-hidden="true" />
                            {formatDisplayDate(block.data)}
                          </span>
                        </div>
                        <div className="block-card-body">
                          <div className="block-field">
                            <span className="block-field-label">Sala</span>
                            <span className="block-field-value">
                              <MapPin size={12} aria-hidden="true" />
                              {getSalaNome(block.sala_id)}
                            </span>
                          </div>
                          {horarioInfo.intervalo && (
                            <div className="block-field">
                              <span className="block-field-label">Horário</span>
                              <span className="block-field-value">
                                <Clock size={12} aria-hidden="true" />
                                {horarioInfo.intervalo}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="icon-button danger"
                        onClick={() => handleDelete(block.id)}
                        aria-label={`Remover bloqueio em ${formatDisplayDate(block.data)}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>

      {deleteTargetId !== null && (() => {
        const target = blocks.find(b => b.id === deleteTargetId);
        const targetInfo = target ? getHorarioInfo(target) : null;
        const targetSala = target ? getSalaNome(target.sala_id) : '';
        const targetDate = target ? formatDisplayDate(target.data) : '';
        return (
          <div
            className="block-delete-overlay"
            role="dialog"
            aria-modal="true"
            onClick={cancelDelete}
          >
            <div
              className="block-delete-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="block-delete-close"
                onClick={cancelDelete}
                disabled={isDeleting}
                aria-label="Fechar"
              >
                <X size={16} />
              </button>
              <div className="block-delete-body">
                <div className="block-delete-icon">
                  <AlertTriangle size={20} />
                </div>
                <div className="block-delete-text">
                  <h4>Remover bloqueio?</h4>
                  <p>Esta ação não pode ser desfeita. O turno selecionado voltará a aceitar novas reservas.</p>
                  {target && (
                    <div className="block-delete-summary">
                      <span className="block-delete-summary-row">
                        <span className="block-delete-summary-label">Turno</span>
                        <strong>{targetInfo?.turnoLabel}{targetInfo?.intervalo ? ` · ${targetInfo.intervalo}` : ''}</strong>
                      </span>
                      <span className="block-delete-summary-row">
                        <span className="block-delete-summary-label">Sala</span>
                        <strong>{targetSala}</strong>
                      </span>
                      <span className="block-delete-summary-row">
                        <span className="block-delete-summary-label">Data</span>
                        <strong>{targetDate}</strong>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="block-delete-actions">
                <button
                  type="button"
                  className="block-delete-btn block-delete-btn-cancel"
                  onClick={cancelDelete}
                  disabled={isDeleting}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="block-delete-btn block-delete-btn-confirm"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Removendo…' : 'Sim, remover'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default BlockManagementModal;
